import { css } from '@inter-ikea-kompis/web-component';

import type IIconPillStyleOptions from '../../types/IIconPillStyleOptions.js';

const BUTTON_HEIGHT = {
	small: 40,
	medium: 56
};

export default class IconPillStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Options object.
	 * @param options.theme Theme.
	 * @param options.isDisabled True if disabled.
	 * @param options.icon Value of the icon prop.
	 * @param options.iconPillSize Icon pill size.
	 * @returns Styles.
	 */

	public static getStyle(options: IIconPillStyleOptions): string {
		const { color, border, motion } = options.theme;

		return css`
			:host {
				display: inline-flex;
				pointer-events: ${options.isDisabled ? 'none' : 'initial'};
			}

			.iconPillButton {
				position: relative;
				min-height: ${40 / 16}rem;
				padding: 0;
				outline: none;
				border-radius: ${border.radius.radius64 / 16}rem;
				border: ${border.thickness.thin / 16}rem solid transparent;

				/* Disabled text selection */
				-webkit-touch-callout: none;
				-webkit-user-select: none;
				-khtml-user-select: none;
				-moz-user-select: none;
				-ms-user-select: none;
				user-select: none;

				display: inline-flex;

				cursor: ${options.isDisabled ? 'default' : 'pointer'};
				-webkit-tap-highlight-color: transparent;

				vertical-align: middle;
				justify-content: center;
				align-items: center;

				transition-property: background, transform;
				transition-duration: ${motion.duration.xsmall};
				transition-timing-function: ${motion.ease.easy};

				background: ${options.isDisabled ? color.disabled.default : color.neutral.grey100};
				color: ${options.isDisabled ? color.neutral.grey500 : color.neutral.grey900};

				box-sizing: border-box;
				height: ${(options.iconPillSize === 'small' ? BUTTON_HEIGHT.small : BUTTON_HEIGHT.medium) /
				16}rem;
				width: ${(options.iconPillSize === 'small' ? BUTTON_HEIGHT.small : BUTTON_HEIGHT.medium) /
				16}rem;
			}

			${this.getSelectedStyle(options)}
			${this.getHover(options)}
			${this.getFocusStyle(options)}
		`;
	}

	/**
	 * Returns the style when selected.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getSelectedStyle(options: IIconPillStyleOptions): string {
		if (!options.selected) {
			return '';
		}
		const { motion, color, border } = options.theme;
		return css`
			.iconPillButton {
				transition-property: border, transform;
				transition-duration: ${motion.duration.xsmall};
				transition-timing-function: ${motion.ease.easy};
				border: ${border.thickness.thin / 16}rem solid
					${options.isDisabled ? color.neutral.grey100 : color.neutral.grey900};
			}
		`;
	}

	/**
	 * Returns the style for focusing the button.
	 *
	 * @param options Options.
	 * @returns Styles.
	 */
	private static getFocusStyle(options: IIconPillStyleOptions): string {
		if (!options.hasFocus) {
			return '';
		}
		const { focusOutline, motion, border } = options.theme;
		return css`
			.outline {
				position: absolute;
				left: -${focusOutline.offset / 16}rem;
				top: -${focusOutline.offset / 16}rem;
				right: -${focusOutline.offset / 16}rem;
				bottom: -${focusOutline.offset / 16}rem;
				z-index: inherit;
				border-radius: ${border.radius.radius64 / 16}rem;
				pointer-events: none;
				transition-property: top, left, right, bottom, opacity;
				transition-duration: ${motion.duration.large};
				box-shadow: 0 0 0 0.1rem ${focusOutline.color};
			}
		`;
	}

	/**
	 * Returns hover effect.
	 *
	 * @param options Options object.
	 * @param options.theme Theme.
	 * @param options.disabled ...
	 * @returns Css code.
	 */
	private static getHover(options: IIconPillStyleOptions): string {
		const { color } = options.theme;
		const inner = css`
			.iconPillButton:hover {
				background: ${options.isDisabled ? color.neutral.grey100 : color.neutral.grey200};
			}
			.iconPillButtonSelected:hover {
				background: ${options.isDisabled ? color.neutral.grey100 : color.neutral.grey200};
			}
			.iconPillButton:active {
				background: ${options.isDisabled ? color.neutral.grey100 : color.neutral.grey300};
			}
			.iconPillButtonSelected:active {
				background: ${options.isDisabled ? color.neutral.grey100 : color.neutral.grey300};
			}
		`;
		return css`
			/* Prevent hover effect on touch. */
			@media (-moz-touch-enabled: 0), (pointer: fine) {
				${inner}
			}
			/* Needed for IE11 to get an hover effect. */
			@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
				${inner}
			}
		`;
	}
}
